import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useInView } from '../../../hooks/useInView';

interface AdvCard {
  icon?: string;
  highlight?: string;
  highlightSuffix?: string;
  title: string;
  desc: string;
  accent: string;
  bgImage?: string;
  large?: boolean;
}

export default function AdvantagesSection() {
  const { t } = useTranslation();
  const [ref, isInView] = useInView({ threshold: 0.05 });

  const cards: AdvCard[] = [
    {
      highlight: '15',
      highlightSuffix: ' років',
      title: t('advantages.adv1Title'),
      desc: t('advantages.adv1Desc'),
      accent: 'from-sky-400 to-cyan-300',
      icon: 'ri-award-line',
      bgImage: 'https://public.readdy.ai/ai/img_res/1fa2125e681c88c22d1bef591fdfc20c.jpg',
      large: true,
    },
    {
      icon: 'ri-building-4-line',
      title: t('advantages.adv8Title'),
      desc: t('advantages.adv8Desc'),
      accent: 'from-emerald-400 to-teal-300',
      bgImage: 'https://readdy.ai/api/search-image?query=dark%20teal%20abstract%20background%20with%20handshake%20silhouette%20and%20glowing%20dots%20deep%20dark%20minimal%20tech&width=600&height=400&seq=adv8bg&orientation=landscape',
    },
    {
      highlight: '80%',
      title: t('advantages.adv4Title'),
      desc: t('advantages.adv4Desc'),
      accent: 'from-cyan-400 to-sky-300',
      icon: 'ri-flashlight-line',
      bgImage: 'https://readdy.ai/api/search-image?query=dark%20blue%20background%20speedometer%20gauge%20glow%20streak%20light%20comet%20trail%20dark%20navy%20tech%20minimal&width=600&height=400&seq=adv4bg&orientation=landscape',
    },
    {
      icon: 'ri-map-pin-2-line',
      title: t('advantages.adv5Title'),
      desc: 'м.Київ, вул.Велика Васильківська 46 · метро Площа Українських Героїв',
      accent: 'from-sky-400 to-indigo-400',
      bgImage: 'https://readdy.ai/api/search-image?query=dark%20navy%20abstract%20background%20glowing%20location%20pin%20dot%20map%20minimal%20dark%20blue%20tech%20glow&width=600&height=400&seq=adv5bg&orientation=landscape',
    },
    {
      icon: 'ri-tools-line',
      highlight: '10 000+',
      title: 'відремонтованих пристроїв',
      desc: '98% успішних ремонтів',
      accent: 'from-violet-400 to-purple-300',
      bgImage: 'https://readdy.ai/api/search-image?query=dark%20background%20laptop%20tablet%20devices%20glowing%20screen%20dark%20navy%20minimal%20tech%20abstract%20glow&width=600&height=400&seq=adv10bg&orientation=landscape',
      large: true,
    },
    {
      icon: 'ri-cpu-line',
      title: t('advantages.adv3Title'),
      desc: t('advantages.adv3Desc'),
      accent: 'from-fuchsia-400 to-pink-300',
      bgImage: 'https://readdy.ai/api/search-image?query=dark%20background%20SSD%20RAM%20memory%20computer%20parts%20glowing%20neon%20purple%20dark%20tech%20minimal%20macro&width=600&height=400&seq=adv3bg&orientation=landscape',
    },
  ];

  return (
    <section
      id="advantages"
      ref={ref}
      className="py-20 lg:py-28 relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #0a0f1e 0%, #0d1530 50%, #0a1628 100%)' }}
    >
      {/* Background glow blobs */}
      <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full opacity-10 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #38bdf8 0%, transparent 70%)' }} />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full opacity-10 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #06b6d4 0%, transparent 70%)' }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <span className="inline-block text-sky-400 text-xs font-bold tracking-[0.2em] uppercase mb-4">
            {t('advantages.label')}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-white">
            {t('advantages.title')}
          </h2>
          <div className="w-12 h-0.5 bg-gradient-to-r from-sky-500 to-cyan-400 mt-5 mx-auto rounded-full" />
        </motion.div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
          {cards.map((card, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 24 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: i * 0.07 }}
              className={card.large ? 'sm:col-span-1 lg:col-span-1' : ''}
            >
              <div className="group relative h-full min-h-[200px] rounded-2xl overflow-hidden cursor-default"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
              >
                {/* BG image */}
                {card.bgImage && (
                  <div className="absolute inset-0 w-full h-full">
                    <img
                      src={card.bgImage}
                      alt=""
                      className="w-full h-full object-cover object-center opacity-50 group-hover:opacity-60 transition-opacity duration-500"
                    />
                    {/* Dense overlay at bottom where text sits, lighter at top */}
                    <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(10,15,30,0.35) 0%, rgba(10,15,30,0.55) 45%, rgba(10,15,30,0.88) 100%)' }} />
                    {/* Extra subtle vignette on sides */}
                    <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, transparent 40%, rgba(10,15,30,0.5) 100%)' }} />
                  </div>
                )}

                {/* Content */}
                <div className="relative z-10 p-6 flex flex-col h-full gap-3">
                  {/* Icon badge */}
                  <div className={`w-11 h-11 flex items-center justify-center rounded-xl bg-gradient-to-br ${card.accent} shrink-0`}
                    style={{ boxShadow: '0 4px 20px rgba(56,189,248,0.25)' }}>
                    <i className={`${card.icon} text-white text-xl`} />
                  </div>

                  {/* Highlight number */}
                  {card.highlight && (
                    <div className={`text-4xl font-extrabold bg-gradient-to-r ${card.accent} bg-clip-text text-transparent leading-none`}>
                      {card.highlight}
                      {card.highlightSuffix && (
                        <span className="text-xl font-bold ml-1">{card.highlightSuffix}</span>
                      )}
                    </div>
                  )}

                  <div>
                    <h3 className="font-bold text-white text-base leading-snug mb-1" style={{ textShadow: '0 1px 8px rgba(0,0,0,0.8)' }}>
                      {card.title}
                    </h3>
                    <p className="text-slate-300 text-sm leading-relaxed" style={{ textShadow: '0 1px 6px rgba(0,0,0,0.9)' }}>
                      {card.desc}
                    </p>
                  </div>
                </div>

                {/* Hover border glow */}
                <div className={`absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-400 pointer-events-none`}
                  style={{ boxShadow: 'inset 0 0 0 1px rgba(56,189,248,0.3)' }} />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom trust bar */}
        <motion.a
          href="https://maps.app.goo.gl/YiVLgNm1ezyHjTGD6"
          target="_blank"
          rel="nofollow noopener noreferrer"
          initial={{ opacity: 0, y: 16 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 py-5 px-8 rounded-2xl cursor-pointer hover:border-sky-400/30 transition-all duration-300"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-sky-400 to-cyan-300 shrink-0">
            <i className="ri-shield-check-line text-white text-lg" />
          </div>
          <div className="text-center sm:text-left">
            <span className="text-white font-medium text-sm">
              Нам довіряють понад{' '}
              <span className="font-extrabold text-sky-400 text-base">5 000 клієнтів</span>
              {' '}у Києві
            </span>
            <div className="flex items-center justify-center sm:justify-start gap-1 mt-1">
              <span className="text-slate-400 text-xs mr-1">Рейтинг</span>
              <span className="text-sky-400 font-bold text-sm">4,9</span>
              {[1,2,3,4,5].map(s => (
                <i key={s} className="ri-star-fill text-amber-400 text-sm" />
              ))}
            </div>
          </div>
        </motion.a>
      </div>
    </section>
  );
}
